import { useCallback, useMemo } from 'react'
import { useChainId, useAccount } from 'wagmi'
import Web3 from 'web3'

import { config, getCorrespondChain } from '@/config'
import Erc20Abi from '@/config/erc20.json'
import type { RawObject } from '@/types'
import type { TokenType } from '@/types/token-type'
import type { Instance } from '@/types/web3-type'
import type { RewardTokenName, Pool } from '@/types/xwan-farming-type'

const defaultNetworkVersion = '1'

export const useWeb3 = () => {
  const { givenProvider } = Web3
  return useMemo(() => new Web3(givenProvider), [givenProvider])
}

export const useNewWeb3 = () => {
  const { address } = useAccount()
  const chainId = useChainId()

  return useMemo(() => {
    const { givenProvider } = Web3
    if (address) {
      return new Web3(givenProvider)
    }
    if (chainId && config[chainId]?.gwan) {
      return new Web3(config[chainId].gwan)
    }
    return new Web3(givenProvider)
  }, [chainId, address])
}

export const useWanWeb3 = () => {
  const chainId = getCorrespondChain.get(
    window.ethereum?.networkVersion || defaultNetworkVersion,
  )

  return useMemo(() => {
    if (chainId) {
      return new Web3(config[chainId].gwan)
    }
    return null
  }, [chainId])
}

export const callContractFunc = async (
  contractName: string,
  endpoint: string,
  instance: Instance,
) => {
  const { web3, params } = instance
  const networkVersion =
    window.ethereum?.networkVersion || defaultNetworkVersion
  if (typeof config[networkVersion] !== 'object') {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ message: 'Network Error' })
  }
  const contractConfig = config[networkVersion][contractName]
  //@ts-ignore
  const contractInstance = new web3.eth.Contract(
    contractConfig.abi,
    contractConfig.addr,
  )

  if (params) {
    return contractInstance.methods[endpoint](...params)
      .call()
      .then((ret: any) => ret)
      .catch(console.log)
  } else {
    return contractInstance.methods[endpoint]()
      .call()
      .then((ret: any) => ret)
      .catch(console.log)
  }
}

export const useConfig = (key: string): RawObject<any> | null => {
  const chainId = useChainId()
  return useMemo(
    () => (config[chainId] ? config[chainId][key] : null),
    [chainId, key],
  )
}

export const useRecordAirDropContract = () => {
  const chainId = getCorrespondChain.get(
    window.ethereum?.networkVersion || defaultNetworkVersion,
  )
  const contractConfig = useConfig('RecordAirDrop')

  return useMemo(() => {
    if (chainId && contractConfig) {
      const web3 = new Web3(config[chainId].gwan)
      return [
        new web3.eth.Contract(contractConfig.abi, contractConfig.addr),
        contractConfig.addr,
      ] as const
    }
    return []
  }, [chainId, contractConfig])
}

export const useXWANConfig = (
  key: string,
  chainId?: string,
): RawObject<any> | null => {
  return useMemo(() => {
    const id = chainId ? chainId : '999'
    return config[id] ? config[id][key] : null
  }, [chainId, key])
}

export const useStoremanGroupDelegateContract = () => {
  const chainId = getCorrespondChain.get(
    window.ethereum?.networkVersion || defaultNetworkVersion,
  )
  const contractConfig = useConfig('StoremanGroupDelegate')

  return useMemo(() => {
    if (chainId && contractConfig) {
      const web3 = new Web3(config[chainId].gwan)
      return [
        new web3.eth.Contract(contractConfig.abi, contractConfig.addr),
        contractConfig.addr,
      ] as const
    }
    return []
  }, [chainId, contractConfig])
}

export const useXWanFarmingPool = () => {
  const chainId = useChainId()
  const contractConfig = useXWANConfig(
    'XWanFarmingContract',
    chainId.toString(),
  )
  const defaultConfig = useXWANConfig('XWanFarmingContract', '888')
  return useMemo(() => {
    if (contractConfig) {
      const pool: Pool = contractConfig.pool
      return pool
    } else {
      const pool: Pool = defaultConfig?.pool
      return pool
    }
  }, [contractConfig, chainId])
}

export const useXWanFarmingPoolInfoFunc = () => {
  const chainId = useChainId()
  const web3 = useNewWeb3()
  const contractConfig = useXWANConfig(
    'XWanFarmingContract',
    chainId.toString(),
  )
  return useMemo(() => {
    if (chainId && contractConfig && web3) {
      const pool: Pool = contractConfig.pool
      const poolArr: RewardTokenName[] = Object.keys(pool) as RewardTokenName[]
      const clientArr: any[] = []
      poolArr.forEach((v: RewardTokenName) => {
        const { contract, addr, rewardAddr } = contractConfig.pool[v]
        const arr = [
          new web3.eth.Contract(contractConfig.abi, contract),
          contract,
          addr,
          rewardAddr,
        ] as const
        clientArr.push(arr)
      })
      return clientArr
    }
    return []
  }, [contractConfig, chainId, web3])
}

export const useXWanSwapFunc = () => {
  const chainId = useChainId()
  const web3 = useNewWeb3()
  const contractConfig = useXWANConfig('XWANSwapContract', chainId.toString())
  return useMemo(() => {
    if (chainId && contractConfig && web3) {
      return [
        new web3.eth.Contract(contractConfig.abi, contractConfig.addr),
        contractConfig.addr,
      ] as const
    }
    return []
  }, [contractConfig, web3?.eth?.Contract, chainId])
}

export const useErc20Contract = (tokenType: TokenType) => {
  const web3 = useWeb3()
  const contractConfig = useConfig('tokenAddrArr')

  return useMemo(() => {
    if (contractConfig && tokenType !== 'WAN') {
      return [
        //@ts-ignore
        new web3.eth.Contract(Erc20Abi, contractConfig[tokenType]),
        contractConfig[tokenType],
      ] as const
    }
    return []
  }, [contractConfig, tokenType, web3.eth.Contract])
}

export const useErc20NewContract = (tokenType: TokenType) => {
  const chainId = useChainId()
  const web3 = useNewWeb3()
  const contractConfig = useXWANConfig('tokenAddrArr', chainId.toString())
  return useMemo(() => {
    if (contractConfig && tokenType !== 'WAN') {
      return [
        //@ts-ignore
        new web3.eth.Contract(Erc20Abi, contractConfig[tokenType]),
        contractConfig[tokenType],
      ] as const
    }
    return []
  }, [contractConfig, tokenType, web3.eth.Contract, chainId])
}

export const useCallContractFunc = () => {
  const web3 = useWeb3()
  return useCallback(
    async (
      ...[contractName, endpoint, instance]: Parameters<typeof callContractFunc>
    ) => callContractFunc(contractName, endpoint, { ...instance, web3 }),
    [web3],
  )
}

export const useRecordAirDropFunc = () => {
  const [contractInstance] = useRecordAirDropContract()
  return useCallback(
    (endpoint: string, params: string[]) =>
      contractInstance.methods[endpoint](...params)
        .call()
        .then((ret: any) => ret),
    [contractInstance],
  )
}

export const useRecordAirDropEvent = () => {
  const [contractInstance] = useRecordAirDropContract()
  return useCallback(
    async (event: string, options: object) =>
      contractInstance.getPastEvents(event, options).then((ret: any) => ret),
    [contractInstance],
  )
}

export const useStoremanGroupDelegateFunc = () => {
  const [contractInstance] = useStoremanGroupDelegateContract()
  return useCallback(
    (endpoint: string, params: string[]) =>
      contractInstance.methods[endpoint](...params)
        .call()
        .then((ret: any) => ret),
    [contractInstance],
  )
}
